import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '../lib/errors';
import {
  ATIVIDADE_TIPOS,
  CRM_ETAPAS,
  CRM_ETAPA_LABELS,
  CRM_ETAPA_TONES,
  type AtividadeRow,
  type ContatoRow,
  type EmpresaRow,
  type OportunidadeRow,
} from '../types';
import TimelineComunicacoes from './TimelineComunicacoes';

interface EmpresaModalProps {
  empresaId: number;
  password: string;
  onClose: () => void;
  onChanged: () => void;
}

interface Detalhe {
  empresa: EmpresaRow;
  atividades: AtividadeRow[];
  quotes: any[];
  orders: any[];
}

function formatBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export default function EmpresaModal({ empresaId, password, onClose, onChanged }: EmpresaModalProps) {
  const [detalhe, setDetalhe] = useState<Detalhe | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newAtividade, setNewAtividade] = useState('');
  const [newTipo, setNewTipo] = useState('tarefa');
  const [addingAct, setAddingAct] = useState(false);

  const [newContatoNome, setNewContatoNome] = useState('');
  const [newContatoCargo, setNewContatoCargo] = useState('');
  const [newContatoEmail, setNewContatoEmail] = useState('');
  const [newContatoTelefone, setNewContatoTelefone] = useState('');
  const [addingContato, setAddingContato] = useState(false);

  const [sugerindoProxima, setSugerindoProxima] = useState(false);
  const [sugestaoProxima, setSugestaoProxima] = useState<any>(null);

  const [activeTab, setActiveTab] = useState<'timeline' | 'comunicacoes' | 'propostas' | 'pedidos'>('timeline');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: { action: 'get_empresa', password, empresa_id: empresaId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = resp as Detalhe & { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setDetalhe(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar empresa.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  const moveEtapa = async (oportunidadeId: number, etapa: string) => {
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: { action: 'update_oportunidade', password, oportunidade_id: oportunidadeId, etapa },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      await load();
      onChanged();
    } catch {
      // silencioso
    }
  };

  const addAtividade = async () => {
    if (!newAtividade.trim()) return;
    setAddingAct(true);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: {
          action: 'create_atividade',
          password,
          empresa_id: empresaId,
          tipo: newTipo,
          conteudo: newAtividade.trim(),
          data: new Date().toISOString(),
          prioridade: 'media',
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      setNewAtividade('');
      await load();
      onChanged();
    } catch {
      // silencioso
    } finally {
      setAddingAct(false);
    }
  };

  const addContato = async () => {
    if (!newContatoNome.trim()) return;
    setAddingContato(true);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-core', {
        body: {
          action: 'create_contato',
          password,
          empresa_id: empresaId,
          nome: newContatoNome.trim(),
          cargo: newContatoCargo.trim(),
          email: newContatoEmail.trim(),
          telefone: newContatoTelefone.trim(),
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      setNewContatoNome('');
      setNewContatoCargo('');
      setNewContatoEmail('');
      setNewContatoTelefone('');
      await load();
      onChanged();
    } catch {
      // silencioso
    } finally {
      setAddingContato(false);
    }
  };

  const toggleAtividade = async (id: number, done: boolean) => {
    try {
      await supabase.functions.invoke('crm-core', {
        body: { action: 'toggle_atividade', password, atividade_id: id, done },
      });
      await load();
      onChanged();
    } catch {
      // silencioso
    }
  };

  const sugerirProximaAcao = async () => {
    setSugerindoProxima(true);
    setSugestaoProxima(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-ai', {
        body: { action: 'proxima_acao', password, empresa_id: empresaId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as any;
      if (parsed?.error) throw new Error(parsed.error);
      setSugestaoProxima(parsed);
    } catch {
      // silencioso
    } finally {
      setSugerindoProxima(false);
    }
  };

  const addSugestaoComoAtividade = async () => {
    if (!sugestaoProxima) return;
    const followUp = new Date();
    followUp.setDate(followUp.getDate() + (Number(sugestaoProxima.prazo_dias ?? 2) || 2));
    try {
      await supabase.functions.invoke('crm-core', {
        body: {
          action: 'create_atividade',
          password,
          empresa_id: empresaId,
          tipo: 'tarefa',
          conteudo: sugestaoProxima.proxima_acao ?? 'Próxima ação sugerida pela IA',
          data: followUp.toISOString(),
          follow_up_at: followUp.toISOString(),
          prioridade: 'alta',
          origem: 'ia',
        },
      });
      setSugestaoProxima(null);
      await load();
      onChanged();
    } catch {
      // silencioso
    }
  };

  const TAB_CONFIG = [
    { key: 'timeline' as const, label: 'Timeline', icon: 'ri-time-line', count: detalhe?.atividades?.length ?? 0 },
    { key: 'comunicacoes' as const, label: 'Comunicações', icon: 'ri-mail-send-line', count: undefined },
    { key: 'propostas' as const, label: 'Propostas', icon: 'ri-file-list-3-line', count: detalhe?.quotes?.length ?? 0 },
    { key: 'pedidos' as const, label: 'Pedidos', icon: 'ri-shopping-bag-3-line', count: detalhe?.orders?.length ?? 0 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full max-w-4xl bg-white rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-hop-border px-6 py-4 flex items-center justify-between gap-4 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-xl">
              <i className="ri-building-4-line text-lg"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">{detalhe?.empresa.nome ?? 'Carregando...'}</h3>
              {detalhe?.empresa.cidade && (
                <p className="text-xs text-gray-500">{detalhe.empresa.cidade}{detalhe.empresa.uf ? `-${detalhe.empresa.uf}` : ''}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
          )}

          {loading && !detalhe ? (
            <div className="flex justify-center py-12">
              <div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : detalhe ? (
            <>
              {/* Info principal */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-hop-bg-alt rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Score</p>
                  <p className="text-xl font-bold text-gray-900">{detalhe.empresa.score}</p>
                </div>
                <div className="bg-hop-bg-alt rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Valor potencial</p>
                  <p className="text-lg font-bold text-gray-900">{formatBRL(Number(detalhe.empresa.valor_potencial ?? 0))}</p>
                </div>
                <div className="bg-hop-bg-alt rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Tipo</p>
                  <p className="text-lg font-bold text-gray-900 capitalize">{detalhe.empresa.tipo ?? 'Privado'}</p>
                </div>
                <div className="bg-hop-bg-alt rounded-xl p-3">
                  <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Contatos</p>
                  <p className="text-lg font-bold text-gray-900">{detalhe.empresa.contatos?.length ?? 0}</p>
                </div>
              </div>

              {detalhe.empresa.cnpj && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <i className="ri-briefcase-line text-gray-400"></i>
                  CNPJ: {detalhe.empresa.cnpj}
                </div>
              )}

              {/* Oportunidades */}
              {detalhe.empresa.oportunidades?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Oportunidades</h4>
                  <div className="space-y-2">
                    {detalhe.empresa.oportunidades.map((o: OportunidadeRow) => (
                      <div key={o.id} className="flex items-center justify-between gap-3 border border-hop-border rounded-xl p-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{o.titulo || detalhe.empresa.nome}</p>
                          {o.valor_potencial != null && (
                            <p className="text-xs text-gray-500">{formatBRL(o.valor_potencial)}</p>
                          )}
                        </div>
                        <select
                          value={o.etapa}
                          onChange={(e) => moveEtapa(o.id, e.target.value)}
                          className={`text-xs font-semibold border rounded-full px-3 py-1.5 cursor-pointer focus:outline-none ${CRM_ETAPA_TONES[o.etapa] ?? ''}`}
                        >
                          {CRM_ETAPAS.map((e) => (
                            <option key={e.key} value={e.key}>{e.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contatos */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Contatos</h4>
                </div>
                {detalhe.empresa.contatos?.length > 0 ? (
                  <div className="space-y-2">
                    {detalhe.empresa.contatos.map((c: ContatoRow) => (
                      <div key={c.id} className="flex items-center justify-between gap-3 border border-hop-border rounded-xl p-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{c.nome || 'Sem nome'}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {[c.cargo, c.departamento].filter(Boolean).join(' · ') || 'Sem cargo'}
                          </p>
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 shrink-0">
                          {c.telefone && <span>{c.telefone}</span>}
                          {c.email && <span className="truncate max-w-[140px]">{c.email}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Nenhum contato ainda.</p>
                )}

                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <input
                    type="text"
                    value={newContatoNome}
                    onChange={(e) => setNewContatoNome(e.target.value)}
                    placeholder="Nome"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                  />
                  <input
                    type="text"
                    value={newContatoCargo}
                    onChange={(e) => setNewContatoCargo(e.target.value)}
                    placeholder="Cargo"
                    className="w-36 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                  />
                  <input
                    type="text"
                    value={newContatoEmail}
                    onChange={(e) => setNewContatoEmail(e.target.value)}
                    placeholder="E-mail"
                    className="w-44 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                  />
                  <input
                    type="text"
                    value={newContatoTelefone}
                    onChange={(e) => setNewContatoTelefone(e.target.value)}
                    placeholder="Telefone"
                    className="w-36 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                  />
                  <button
                    onClick={addContato}
                    disabled={addingContato || !newContatoNome.trim()}
                    className="inline-flex items-center justify-center gap-1 bg-[#E8610A] text-white text-sm font-semibold px-3 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {addingContato ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b border-hop-border">
                <div className="flex gap-1 overflow-x-auto">
                  {TAB_CONFIG.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors border-b-2 ${
                        activeTab === tab.key
                          ? 'border-[#E8610A] text-[#E8610A]'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <i className={tab.icon}></i>
                      {tab.label}
                      {tab.count !== undefined && tab.count > 0 && (
                        <span className="bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 text-[10px]">
                          {tab.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab: Timeline */}
              {activeTab === 'timeline' && (
                <div>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Timeline</h4>
                    <button
                      onClick={sugerirProximaAcao}
                      disabled={sugerindoProxima}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2 py-1 rounded-lg hover:bg-[#E8610A]/5 cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      <i className={`ri-sparkling-2-line ${sugerindoProxima ? 'animate-pulse' : ''}`}></i>
                      {sugerindoProxima ? 'Pensando...' : 'Sugerir próxima ação'}
                    </button>
                  </div>

                  {sugestaoProxima && (
                    <div className="mb-3 bg-[#E8610A]/5 border border-[#E8610A]/20 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{sugestaoProxima.proxima_acao}</p>
                          {sugestaoProxima.motivo && (
                            <p className="text-xs text-gray-600 mt-1">{sugestaoProxima.motivo}</p>
                          )}
                          {sugestaoProxima.mensagem_pronta && (
                            <p className="text-xs text-gray-600 mt-2 bg-white border border-hop-border rounded-lg p-2 whitespace-pre-wrap">
                              {sugestaoProxima.mensagem_pronta}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={addSugestaoComoAtividade}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-3 py-2 rounded-lg cursor-pointer whitespace-nowrap shrink-0"
                        >
                          <i className="ri-add-line"></i>
                          Agendar
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2 mb-3">
                    <select
                      value={newTipo}
                      onChange={(e) => setNewTipo(e.target.value)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer"
                    >
                      {Object.entries(ATIVIDADE_TIPOS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={newAtividade}
                      onChange={(e) => setNewAtividade(e.target.value)}
                      placeholder="Ex: Ligar para confirmar reunião..."
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
                    />
                    <button
                      onClick={addAtividade}
                      disabled={addingAct || !newAtividade.trim()}
                      className="inline-flex items-center justify-center gap-1 bg-[#E8610A] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#C4500A] cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {addingAct ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>}
                    </button>
                  </div>
                  {detalhe.atividades.length === 0 ? (
                    <p className="text-sm text-gray-400">Nenhuma atividade registrada.</p>
                  ) : (
                    <div className="space-y-2">
                      {detalhe.atividades.map((a) => {
                        const tipo = ATIVIDADE_TIPOS[a.tipo] ?? ATIVIDADE_TIPOS.nota;
                        return (
                          <div key={a.id} className="flex items-start gap-3 border border-hop-border rounded-xl p-3">
                            <button
                              onClick={() => toggleAtividade(a.id, !a.done)}
                              className={`w-6 h-6 shrink-0 flex items-center justify-center border rounded-full cursor-pointer ${
                                a.done ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 text-transparent hover:border-green-500'
                              }`}
                            >
                              <i className="ri-check-line text-xs"></i>
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">
                                  <i className={tipo.icon}></i>
                                  {tipo.label}
                                </span>
                                <span className="text-xs text-gray-400">{formatDateTime(a.data)}</span>
                              </div>
                              <p className={`text-sm text-gray-700 mt-1 ${a.done ? 'line-through text-gray-400' : ''}`}>
                                {a.conteudo}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Comunicações */}
              {activeTab === 'comunicacoes' && (
                <TimelineComunicacoes
                  empresaId={empresaId}
                  onDataChanged={() => {
                    load();
                    onChanged();
                  }}
                />
              )}

              {/* Tab: Propostas */}
              {activeTab === 'propostas' && (
                <div>
                  {detalhe.quotes.length === 0 ? (
                    <p className="text-sm text-gray-400 py-4">Nenhuma proposta ainda.</p>
                  ) : (
                    <div className="space-y-2">
                      {detalhe.quotes.map((q) => (
                        <div key={q.id} className="flex items-center justify-between gap-3 border border-hop-border rounded-xl p-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">Orçamento #{q.id}</p>
                            <p className="text-xs text-gray-500">{formatDate(q.created_at)} · {q.status}</p>
                          </div>
                          <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{formatBRL(Number(q.total ?? 0))}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Pedidos */}
              {activeTab === 'pedidos' && (
                <div>
                  {detalhe.orders.length === 0 ? (
                    <p className="text-sm text-gray-400 py-4">Nenhum pedido ainda.</p>
                  ) : (
                    <div className="space-y-2">
                      {detalhe.orders.map((o) => (
                        <div key={o.id} className="flex items-center justify-between gap-3 border border-hop-border rounded-xl p-3">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900">Pedido #{o.id}</p>
                            <p className="text-xs text-gray-500">{formatDate(o.created_at)} · {o.status}</p>
                          </div>
                          <p className="text-sm font-bold text-gray-900 whitespace-nowrap">{formatBRL(Number(o.subtotal_items ?? 0))}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}